// src/components/issues/ImportConnectionsDialog.jsx
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { CheckCircle2, ExternalLink, RefreshCw } from "lucide-react";
import { getConnectionStatus, updateConnectionStatus } from "../../lib/userConnectionAuth";
import {
  getIntegrationStatus,
  getIntegrationSettings,
  disconnectIntegration,
  clearLegacyIntegrationStorage,
} from "../../lib/integrations";
import { useAuth } from "../../contexts/AuthContext";
import JiraConfigDialog from "./JiraConfigDialog";
import SlackConfigDialog from "./SlackConfigDialog";
import { ProviderLogo } from "../ui/provider-logo";

export default function ImportConnectionsDialog({ open, onClose, onImportJira, onImportSlack }) {
  const [connections, setConnections] = useState({
    jira: { connected: false, lastImport: null, issueCount: 0 },
    slack: { connected: false, lastImport: null, alertCount: 0 }
  });
  const [importing, setImporting] = useState({ jira: false, slack: false });
  const [jiraConfigOpen, setJiraConfigOpen] = useState(false);
  const [slackConfigOpen, setSlackConfigOpen] = useState(false);
  const { user } = useAuth();

  // Helper function to get next auto-import time
  const getNextAutoImport = (service) => {
    if (!user?.id) return null;
    const lastImportKey = `spcc:last-auto-import-${service}:${user.id}`;
    const lastImport = localStorage.getItem(lastImportKey);
    if (!lastImport) return "Will sync on next page load";

    const lastImportTime = parseInt(lastImport);
    const nextImportTime = lastImportTime + (24 * 60 * 60 * 1000); // 24 hours later
    const now = Date.now();

    if (nextImportTime <= now) {
      return "Ready to sync on next page load";
    }

    const hoursUntilNext = Math.ceil((nextImportTime - now) / (60 * 60 * 1000));
    return `Next auto-sync in ~${hoursUntilNext} hour${hoursUntilNext === 1 ? '' : 's'}`;
  };

  // Connection state comes from the integrations API; import stats are kept locally.
  const loadConnections = async () => {
    const stats = getConnectionStatus(user.id);
    const status = await getIntegrationStatus();
    const site = status.jira?.sites?.find(s => s.id === status.jira.site_id);
    setConnections({
      jira: { ...stats.jira, connected: !!status.jira, name: site?.name },
      slack: { ...stats.slack, connected: !!status.slack, name: status.slack?.team?.name },
    });
  };

  useEffect(() => {
    if (open && user?.id) {
      clearLegacyIntegrationStorage(user.id);
      loadConnections().catch(error => console.error('Failed to load connections:', error));
    }
  }, [open, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save import stats for user
  const saveStats = (service, stats) => {
    if (!user?.id) return;
    const saved = updateConnectionStatus(service, stats, user.id);
    setConnections(prev => ({ ...prev, [service]: { ...prev[service], ...saved[service] } }));
  };

  const runImport = async (service, onImport, customConfig) => {
    if (!user?.id) return 0;
    setImporting(prev => ({ ...prev, [service]: true }));
    try {
      const config = customConfig || (await getIntegrationSettings())[service];
      const count = await onImport?.(config);
      saveStats(service, {
        lastImport: new Date().toISOString(),
        [service === 'jira' ? 'issueCount' : 'alertCount']: count || 0
      });
      return count || 0;
    } catch (error) {
      console.error(`${service} import failed:`, error);
      if (error.reconnect) setConnections(prev => ({ ...prev, [service]: { ...prev[service], connected: false } }));
      throw error;
    } finally {
      setImporting(prev => ({ ...prev, [service]: false }));
    }
  };

  const handleJiraImport = (config) => runImport('jira', onImportJira, config);
  const handleSlackImport = (config) => runImport('slack', onImportSlack, config);

  // Called by the config dialogs after "Save & Import".
  const handleConfigSaved = (service, importFn) => async (config) => {
    const count = await importFn(config);
    loadConnections().catch(() => {});
    return count;
  };

  const handleDisconnect = async (service) => {
    if (!user?.id) return;
    try {
      await disconnectIntegration(service);
    } catch (error) {
      console.error(`Failed to disconnect from ${service}:`, error);
    }
    saveStats(service, { lastImport: null, issueCount: 0, alertCount: 0 });
    loadConnections().catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 500 }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: 18, fontWeight: 800 }}>
            Connect & Import
          </DialogTitle>
          <DialogDescription>
            Connect your own Jira and Slack to import issues and alerts.
            <br />
            <span style={{ fontSize: 12, color: "var(--success)", fontWeight: 600, marginTop: 4, display: "inline-block" }}>
              ✓ Auto-import enabled: Connected services will sync automatically every 24 hours
            </span>
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gap: 16 }}>
          {/* JIRA Connection */}
          <div style={{
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 16,
            background: "var(--bg-surface)"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ProviderLogo provider="jira" size={20} />
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>JIRA</h4>
              </div>

              {connections.jira.connected && (
                <Badge style={{ background: "var(--success-soft)", color: "var(--success)", border: 0 }}>
                  <CheckCircle2 size={12} style={{ marginRight: 4 }} />
                  Connected
                </Badge>
              )}
            </div>

            {connections.jira.connected ? (
              <div style={{ display: "grid", gap: 8 }}>
                {connections.jira.name && (
                  <div style={{ fontSize: 13, color: "var(--fg-secondary)" }}>Site: <strong>{connections.jira.name}</strong></div>
                )}
                {connections.jira.lastImport && (
                  <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
                    Last import: {new Date(connections.jira.lastImport).toLocaleString()}
                    {connections.jira.issueCount > 0 && ` (${connections.jira.issueCount} issues)`}
                  </div>
                )}

                <div style={{
                  fontSize: 12,
                  color: "var(--success)",
                  fontWeight: 500,
                  padding: "6px 10px",
                  background: "var(--success-soft)",
                  borderRadius: 6,
                  border: "1px solid var(--success-border)"
                }}>
                  🔄 {getNextAutoImport('jira')}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="sm"
                    onClick={() => handleJiraImport().catch(e => alert(e.message))}
                    disabled={importing.jira}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {importing.jira ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Import Now
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Clear the auto-import timestamp to force a fresh import
                      if (user?.id) {
                        localStorage.removeItem(`spcc:last-auto-import-jira:${user.id}`);
                      }
                      handleJiraImport().catch(e => alert(e.message));
                    }}
                    disabled={importing.jira}
                    style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}
                  >
                    Force Refresh
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDisconnect('jira')}
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{
                  margin: "0 0 12px 0",
                  fontSize: 13,
                  color: "var(--fg-muted)"
                }}>
                  Sign in with your Atlassian account to import tickets from your own Jira.
                </p>

                <Button
                  onClick={() => setJiraConfigOpen(true)}
                  style={{
                    background: "#2684ff",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6
                  }}
                >
                  <ExternalLink size={14} />
                  Connect Jira
                </Button>
              </div>
            )}
          </div>

          {/* Slack Connection */}
          <div style={{
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 16,
            background: "var(--bg-surface)"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ProviderLogo provider="slack" size={20} />
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Slack</h4>
              </div>

              {connections.slack.connected && (
                <Badge style={{ background: "var(--success-soft)", color: "var(--success)", border: 0 }}>
                  <CheckCircle2 size={12} style={{ marginRight: 4 }} />
                  Connected
                </Badge>
              )}
            </div>

            {connections.slack.connected ? (
              <div style={{ display: "grid", gap: 8 }}>
                {connections.slack.name && (
                  <div style={{ fontSize: 13, color: "var(--fg-secondary)" }}>Workspace: <strong>{connections.slack.name}</strong></div>
                )}
                {connections.slack.lastImport && (
                  <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
                    Last import: {new Date(connections.slack.lastImport).toLocaleString()}
                    {connections.slack.alertCount > 0 && ` (${connections.slack.alertCount} alerts)`}
                  </div>
                )}

                <div style={{
                  fontSize: 12,
                  color: "var(--success)",
                  fontWeight: 500,
                  padding: "6px 10px",
                  background: "var(--success-soft)",
                  borderRadius: 6,
                  border: "1px solid var(--success-border)"
                }}>
                  🔄 {getNextAutoImport('slack')}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="sm"
                    onClick={() => handleSlackImport().catch(e => alert(e.message))}
                    disabled={importing.slack}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {importing.slack ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Import Now
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDisconnect('slack')}
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{
                  margin: "0 0 12px 0",
                  fontSize: 13,
                  color: "var(--fg-muted)"
                }}>
                  Sign in with your Slack account to import alerts from your own workspace.
                </p>

                <Button
                  onClick={() => setSlackConfigOpen(true)}
                  style={{
                    background: "#4A154B",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6
                  }}
                >
                  <ExternalLink size={14} />
                  Connect Slack
                </Button>
              </div>
            )}
          </div>

        </div>
      </DialogContent>

      {/* JIRA Configuration Dialog */}
      <JiraConfigDialog
        open={jiraConfigOpen}
        onClose={() => setJiraConfigOpen(false)}
        onSave={handleConfigSaved('jira', handleJiraImport)}
      />

      {/* Slack Configuration Dialog */}
      <SlackConfigDialog
        open={slackConfigOpen}
        onClose={() => setSlackConfigOpen(false)}
        onSave={handleConfigSaved('slack', handleSlackImport)}
      />
    </Dialog>
  );
}